import cx from 'classnames'
import {push, replace} from 'connected-react-router'

/* Constants */
import {MOBILE_COMPONENTS, MOBILE_COMPONENTS_OPTIONS} from '../voltran/voltranConstants'
import {DAVINCI_EVENT_NAMES, DAVINCI_PAGE_TYPES} from '../analytics/davinci/davinciConstants'
import {FEATURE_TOGGLE_NAMES} from '../../modules/feature-toggle/featureToggleConstants'
import {HBUS_CLIENT_TYPES} from '../analytics/hbus/hbusConstants'

/* Utils */
import {getBrowserCookie} from '../../../client/common/persistence/browserCookieUtils'
import {generateComparedProductsForPdp} from './generateComparedProductsForPdp'
import {generateProductStatus} from './productCommonUtils'
import leoProductEvents from '../../events/leo/productEvents';

/* Actions */
import {actionCreators as analyticsActionCreators} from '../../redux/actions/analyticsActions'

/* Styles */
import s from '../../routes/mobile/product-detail/product-detail.scss'

function formatPrice(price, stripDecimals = false) {
  if (isNaN(price)) {
    return null;
  }

  const formatter = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formattedPrice = formatter.format(price);

  if (stripDecimals && formattedPrice.endsWith(',00')) {
    return `${formattedPrice.replace(/,00$/, '')} TL`;
  }

  return `${formattedPrice} TL`;
}

const getShipmentType = ({isInStock, isPreOrder, shipmentInformation}) => {

  if (shipmentInformation?.fastShipping) {
    return 'super-hizli';
  } else if (!isInStock && !isPreOrder) {
    return 'stokta-yok';
  } else if (shipmentInformation?.howManyDaysToShipment > 0) {
    return shipmentInformation.howManyDaysToShipment.toString();
  }

  return ''
}

function generateCommonProps({
  dispatch,
  product,
  pageTypeForDavinci,
  componentData,
  position,
  queryParams,
  fragmentsMap,
  pageFeatureToggleMap,
  userInfo,
  isAvailableProduct,
  isBot,
  isPremium,
  loyaltyInfoPending
}) {
  return {
    dispatch,
    position,
    sku: product?.sku,
    name: product?.name,
    brandName: product?.brand,
    brandUrl: product?.brandId,
    merchant: product.merchant,
    merchantName: product?.merchant?.name,
    merchantId: product?.merchant?.id,
    merchantUrl: product?.merchant?.urlPostfix,
    productId: product?.productId,
    listingId: product?.listingId,
    categories: product?.categories,
    categoryName: product?.categories?.[product?.categories.length - 1]?.categoryName,
    categoryId: product?.categories?.[product?.categories.length - 1]?.categoryId,
    categoryUrlKeyword: product?.categories?.[product?.categories.length - 1]?.urlKeyword,
    pageTypeForDavinci: pageTypeForDavinci,
    price: getProductFinalPrice(product?.prices),
    componentData: componentData,
    productMedia: product?.media,
    availableStock: product?.stockInformation?.availableStock,
    queryParams,
    fragmentsMap,
    pageFeatureToggleMap,
    isAvailableProduct,
    userInfo,
    isBot,
    isPremium,
    loyaltyInfoPending
  }
}

function generateProductDetailCarouselProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  isAdultProduct,
  pageFeatureToggleMap,
  componentData,
  queryParams,
  campaignEvaluateResult,
  isAvailableProduct,
  userInfo,
  isPremium,
  loyaltyInfoPending,
  campaignEvaluateFinalPrice,
  fragments,
  fragmentsMap
}) {
  return {
    className: s.carousel,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      isAvailableProduct,
      queryParams,
      userInfo,
      isPremium,
      loyaltyInfoPending,
      fragmentsMap
    }),
    productAnalytics: product?.productAnalytics,
    imageBadge: product?.imageBadge,
    productMedia: product.media,
    isAdultProduct: isAdultProduct,
    displayGreenBand: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_SPECIAL_CAMPAIGN_ON_PDP_PAGE) && isAvailableProduct,
    campaignEvaluateResult,
    campaignEvaluateFinalPrice,
    fragments,
    checker: true,
  }
}

function generateBuyBoxAreaProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  isLoading,
  queryParams,
  isAvailableProduct,
  seoInformation,
  energyClassBadge,
}) {
  return {
    className: s.buyBoxArea,
    name: seoInformation?.h1 || product?.name,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci}),
    reviews: product?.reviews,
    slugName: product?.slugName,
    energyClassBadge: energyClassBadge,
    h1: seoInformation?.h1,
    queryParams,
    isLoading,
    isAvailableProduct,
    checker: true,
  }
}

function generateBuyBoxSellerProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  fragmentsMap,
  queryParams,
  flags,
  pageFeatureToggleMap,
  isAvailableProduct,
  userInfo
}) {
  return {
    className: s.buyBoxSeller,
    ...generateCommonProps({
      dispatch,
      product,
      position,
      componentData,
      pageTypeForDavinci,
      queryParams,
      pageFeatureToggleMap,
      fragmentsMap,
      userInfo
    }),
    flags,
    product,
    checker: isAvailableProduct,
    wrapperProps: {
      id: 'hyd-buybox-seller'
    }
  }
}

function generateVariantPanelProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  queryParams,
  match,
  variants
}) {
  return {
    className: s.variantPanel,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    match,
    isFashionProduct: product?.isFashionProduct,
    checker: variants && product?.variants?.length > 0,
  }
}

function generateBestReviewProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  componentData,
  queryParams,
  fragmentsMap,
  isAvailableProduct,
  pageFeatureToggleMap,
  isBot
}) {
  const FRAGMENT = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_COMMENTS_NEW_MWEB) ? MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.USER_CONTENT] : MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.BEST_REVIEW]
  return {
    className: cx(s.review, {
      [s.comments]: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_COMMENTS_NEW_MWEB),
      [s.bestReviews]: !pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_COMMENTS_NEW_MWEB)
    }),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    fragmentInfo: fragmentsMap?.get(FRAGMENT?.id?.toString()),
    options: FRAGMENT,
    renderMethodClientSide: false,
    checker: isAvailableProduct,
    wrapperProps: {
      id: 'hyd-best-review'
    }
  }
}

function generateQuestionAndAnswerProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  queryParams,
  fragmentsMap,
  componentData,
  isAvailableProduct,
  isBot
}) {
  return {
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    fragmentInfo: fragmentsMap?.get(MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.QUESTION_AND_ANSWER]?.id?.toString()),
    options: MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.QUESTION_AND_ANSWER],
    renderMethodClientSide: process.env.BROWSER,
    componentData: componentData,
    checker: fragmentsMap?.get(MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.QUESTION_AND_ANSWER]?.id?.toString()) && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-qna'
    }
  }
}

function generateProductBadgesProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  componentData,
  queryParams,
  isAvailableProduct
}) {
  return {
    className: s.productBadges,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    badges: product?.badges,
    checker: isAvailableProduct,
    wrapperProps: {
      id: 'hyd-product-badges'
    }
  }
}

function generateRecommendationProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  componentData,
  queryParams,
  fragmentsMap,
  userInfo,
  isAvailableProduct,
  isBot
}) {
  return {
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    userInfo,
    fragmentsMap,
    isAvailableProduct,
    checker: true,
    wrapperProps: {
      id: `hyd-recommendation-${componentData?.placementId}`
    }
  }
}

function generateSponsoredProductsProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  componentData,
  queryParams,
  fragmentsMap,
}) {
  return {
    className: s.sponsoredProduct,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    fragmentInfo: fragmentsMap?.get(MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.SPONSORED_PRODUCTS]?.id?.toString()),
    options: MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.SPONSORED_PRODUCTS],
    checker: true,
    wrapperProps: {
      id: 'hyd-sponsored-products'
    }
  }
}

function generateMerchantCouponsProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  componentData,
  queryParams,
  pageFeatureToggleMap,
  isAvailableProduct,
  userInfo
}) {
  return {
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, userInfo}),
    checker: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.IS_DISPLAY_MERCHANT_COUPONS_ON_PDP) && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-merchant-coupons'
    }
  }
}

function generateVasProps({
  dispatch,
  position,
  pageTypeForDavinci,
  queryParams,
  componentData,
  product,
  isVasExists,
  isAvailableProduct,
  vas,
  selectedVasProduct,
  pageFeatureToggleMap,
  isInStock,
  isPreOrder,
  shipmentInformation,
}) {
  return {
    className: s.vas,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    definationName: product.name,
    selectedVasProduct,
    productStatus: generateProductStatus(product),
    shippingType: getShipmentType({
      isInStock,
      isPreOrder,
      shipmentInformation,
    }),
    vas,
    renderMethodClientSide: process.env.BROWSER,
    checker: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.USE_VAS_SERVICE) && isVasExists && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-vas'
    }
  }
}

function generatePremiumBannerProps({
  dispatch,
  position,
  pageTypeForDavinci,
  queryParams,
  fragmentsMap,
  componentData,
  pageFeatureToggleMap,
  product,
  userInfo,
  isAvailableProduct,
  isGetCampaignsPdpDashboardPending,
  campaignEvaluateResult,
}) {
  return {
    className: s.premium,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, pageFeatureToggleMap, fragmentsMap}),
    fragmentInfo: fragmentsMap?.get(MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.PREMIUM_BANNER]?.id?.toString()),
    options: MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.PREMIUM_BANNER],
    userId: userInfo?.userId,
    fragmentPayload: {
      ...MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.PREMIUM_BANNER],
      paramater: {
        ...MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.PREMIUM_BANNER].paramater,
      }
    },
    isGetCampaignsPdpDashboardPending,
    campaignEvaluateResult,
    premiumContentRequestPayload: {
      sku: product?.sku,
      userId: userInfo?.userId,
      definitionId: product?.definitionId,
      finalPrice: !isGetCampaignsPdpDashboardPending && campaignEvaluateResult?.evaluateAsPremiumResult?.discountedAmount || campaignEvaluateResult?.evaluateResult?.discountedAmount || getProductFinalPrice(product?.prices),
      merchantId: product?.merchant?.id,
      isMobile: true,
    },
    isVisible: product?.isInStock && !product?.isPreOrder && product?.isProductLive,
    checker: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.IS_DISPLAY_LOYALTY_PREMIUM_ON_PDP) && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-premium'
    }
  }
}

function generateDeliveryOptionsProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  isLoading,
  queryParams,
  isAvailableProduct,
  dueDate,
  deliveryTooltips,
  fragmentsMap
}) {
  return {
    className: cx(s.deliveryOptions, s[componentData?.displayType]),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, fragmentsMap}),
    isLoading: isLoading,
    dueDate,
    deliveryTooltips,
    checker: isAvailableProduct && !product?.nonTransferable,
    wrapperProps: {
      id: 'hyd-delivery-options'
    }
  }
}

function generateTechSpecsProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  componentData,
  queryParams,
  isAvailableProduct,
  pageFeatureToggleMap,
  isBot
}) {
  return {
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    techSpecs: product?.expends?.flatMap?.(item => item.properties),
    enableErrorFeedbackButton: pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.ENABLE_ERROR_FEEDBACK_BUTTON),
    className: cx(s.techSpec),
    checker: product?.expends?.length > 0 && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-tech-specs'
    }
  }
}

function generateProductDescriptionProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  fragmentsMap,
  queryParams,
  isBot
}) {
  return {
    className: s.productDescription,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    fragmentInfo: fragmentsMap?.get(MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.PRODUCT_DESCRIPTION]?.id?.toString()),
    options: MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.PRODUCT_DESCRIPTION],
    scriptsOptions: {
      renderMethodClientSide: process.env.BROWSER
    },
    checker: true,
    wrapperProps: {
      id: 'hyd-product-description'
    }
  }
}

function generatePaymentOptionsProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  fragmentsMap,
  buyback,
  queryParams,
  isLoading,
  isAvailableProduct,
  payments,
  isLoadingPayments,
  pageFeatureToggleMap,
  campaignEvaluateResult,
  campaignEvaluateFinalPrice
}) {
  return {
    className: cx(s.paymentOptions, s[componentData?.displayType]),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    fragmentsMap: fragmentsMap,
    paymentOptions: payments,
    amount: product?.prices?.[product?.prices?.length - 1]?.formattedPrice,
    currentPriceBeforePoint: product?.prices?.[product?.prices?.length - 1]?.formattedPrice?.split(',')[0],
    currentPriceAfterPoint: product?.prices?.[product?.prices?.length - 1]?.formattedPrice?.split(',')[1],
    definitionId: product?.definitionId,
    numberOfInstallments: product?.numberOfInstallments,
    paymentTag: product?.paymentTag,
    consumerFinanceTagList: product?.consumerFinanceTagList,
    buyback: buyback,
    campaignEvaluateResult,
    campaignEvaluateFinalPrice,
    taxVatRate: product?.taxVatRate,
    isLoading: isLoading || isLoadingPayments,
    checker: isAvailableProduct && pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_CARD_AND_INSTALLMENT_INFO_ON_PDP),
    wrapperProps: {
      id: 'hyd-payment-options'
    }
  }
}

function generateCampaignsProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  fragmentsMap,
  queryParams,
  isAvailableProduct,
  campaigns,
  winnerCampaignName,
  isGetCampaignsPdpDashboardPending,
  campaignEvaluateResult
}) {
  return {
    className: cx(s.campaigns, s[componentData?.displayType]),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, fragmentsMap}),
    campaigns,
    winnerCampaignName: campaignEvaluateResult?.evaluateAsPremiumResult?.campaignText || campaignEvaluateResult?.evaluateResult?.campaignText || winnerCampaignName,
    isGetCampaignsPdpDashboardPending,
    checker: isAvailableProduct,
    wrapperProps: {
      id: 'hyd-campaigns'
    }
  }
}

function generateOtherMerchantsProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  history,
  queryParams,
  match,
  dueDate,
  isAvailableProduct,
  allListings,
  isBot
}) {
  return {
    className: cx(s.otherMerchants),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    formattedMinimumPriceOfListing: product?.formattedMinimumPriceOfListing,
    formattedMaximumPriceOfListing: product?.formattedMaximumPriceOfListing,
    isShowPriceRangeOfListings: product?.isShowPriceRangeOfListings,
    listings: product?.listings?.filter(listing => listing?.merchantId !== product?.merchant?.id),
    history,
    match,
    allListings,
    hasMoreListings: product?.hasMoreListings,
    productStatus: generateProductStatus(product),
    reviewRate: `${product?.reviews?.customerReviewScore || ''}`.replace('.', ','),
    reviewcount: `${product?.reviews?.customerReviewCount || ''}`,
    imageUrl: product?.media?.[0]?.url,
    dueDate,
    product,
    checker: product?.listings?.filter(listing => listing?.merchantId !== product?.merchant?.id)?.length > 0 && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-other-merchants'
    }
  }
}

function generateCampaignBundleProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  campaignDetail,
  queryParams,
  isAvailableProduct,
  campaigns,
  bundleCampaign,
  isGetCampaignsPdpDashboardPending,
  userInfo
}) {
  return {
    className: cx(s.campaignBundle),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    prepareProduct: {
      sku: product?.sku,
      productId: product?.productId,
      listingId: product?.listingId,
      quantity: 1,
      imageUrl: product?.media?.[0]?.url || '',
      name: product?.name,
      merchantId: product?.merchantId,
      merchantName: product?.merchantName,
      price: product?.prices?.[0]?.value || '',
    },
    product,
    campaigns,
    bundleCampaign,
    isGetCampaignsPdpDashboardPending,
    userInfo,
    checker: isAvailableProduct,
    wrapperProps: {
      id: 'hyd-buy-together'
    }
  }
}


function generatePopulerKeywordProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  popularKeyword,
  queryParams,
  isAvailableProduct,
  isBot
}) {
  return {
    className: cx(s.popularKeywords),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    keywords: popularKeyword,
    checker: popularKeyword?.length > 0 && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-popular-keywords'
    }
  }
}

function generateComparedProductsProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  isBot,
  queryParams,
  comparedProducts,
  pageFeatureToggleMap,
  match
}) {
  let newComparedProducts = comparedProducts

  if (comparedProducts?.length > 2) {
    newComparedProducts = generateComparedProductsForPdp({
      currentProduct: product,
      comparedProducts,
      currentProductUrl: match?.url,
    })
  }

  return {
    className: cx(s.comparedProductsWrapper),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    products: newComparedProducts,
    title: `${product?.brand} ${product?.name} ile en çok karşılaştırılanlar`,
    categoryId: product?.categories?.[product?.categories.length - 1]?.categoryId,
    brand: product?.brand,
    placeholderClassName: s.comparedProductsPlaceholder,
    pageType: DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
    showLastUpdatedDate: true,
    checker: pageFeatureToggleMap.get(FEATURE_TOGGLE_NAMES.DISPLAY_COMPARED_TOP_SELLER_PRODUCT_PDP),
    wrapperProps: {
      id: 'hyd-compared-products'
    }
  }
}

function generateBuybackRowProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  queryParams,
  buyback,
  componentData,
  fragmentsMap,
  isAvailableProduct
}) {
  let tbaUrl = '';
  if (buyback?.tbaUrl) {
    tbaUrl = new URL(buyback.tbaUrl)
    tbaUrl?.searchParams?.append('isOpenTbaDirectly', true)
  }
  return {
    className: s.buybackRow,
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    fragmentPayload: {
      ...MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.BUYBACK],
      paramater: {
        ...MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.BUYBACK].paramater,
      },
      clientUrl: tbaUrl?.href,
    },
    buyback,
    fragmentInfo: fragmentsMap?.get(MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.BUYBACK]?.id?.toString()),
    options: MOBILE_COMPONENTS_OPTIONS[MOBILE_COMPONENTS.BUYBACK],
    renderMethodClientSide: process.env.BROWSER,
    checker: buyback && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-buyback'
    }
  }
}

function generateMinQuantityProps({
  dispatch,
  product,
  position,
  pageTypeForDavinci,
  queryParams,
  componentData,
  isAvailableProduct
}) {
  return {
    className: cx(s.minQuantity),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams}),
    minimumPurchasableQuantity: product?.minimumPurchasableQuantity,
    checker: product?.minimumPurchasableQuantity > 0 && isAvailableProduct,
    wrapperProps: {
      id: 'hyd-min-quantity'
    }
  }
}

function generateAdsBannerProps({
  dispatch,
  position,
  product,
  pageTypeForDavinci,
  componentData,
  queryParams,
  isAvailableProduct,
  isBot,
  fragmentsMap,
  pageFeatureToggleMap
}) {
  return {
    className: cx(s.adsBanner),
    ...generateCommonProps({dispatch, product, position, componentData, pageTypeForDavinci, queryParams, isBot}),
    fragmentsMap: fragmentsMap,
    checker: pageFeatureToggleMap.get(FEATURE_TOGGLE_NAMES.DISPLAY_ADX_BANNER_MWEB),
    wrapperProps: {
      id: 'hyd-adx'
    }
  }
}

function setAskToSellerButtonAttributes(product) {
  const element = document.querySelector('cst-asktoseller-button')

  if (element) {
    element.setAttribute('buybox', 'true')
    element.setAttribute('merchantarea', 'true')
    element.setAttribute('merchant', JSON.stringify({
      id: product?.merchantId,
      name: product?.merchantName,
    }));
    element.setAttribute('product', JSON.stringify({
      sku: product?.sku,
      name: product?.name,
    }))
  }
}

function sendVariantChangeEvent(product) {
  let variants = [];
  // eslint-disable-next-line array-callback-return
  product?.priceBaseVariantList?.map((variant, index) => {
    // eslint-disable-next-line array-callback-return
    variant?.properties?.map((property) => {
      if (property.isSelected) {
        variants.push({name: variant.name, value: property.name, position: index + 1})
      }
    })
  })
  window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.CHANGE_VARIANT, {
    canonical_url: '',
    category: {
      id: product?.categories?.[product?.categories?.length - 1]?.categoryId,
      name: product?.categories?.[product?.categories?.length - 1]?.categoryName,
    },
    category_hierarchy: product?.categories?.map((item) => (
      {id: item?.categoryId, name: item?.categoryName}
    )),
    page_type: DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
    page_value: product?.sku,
    merchant: {name: product?.merchant?.name, id: product?.merchant?.id},
    isMerchantChanged: false,
    isPriceChanged: false,
    location: DAVINCI_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
    variant: variants,
    products: [{
      listing_id: product?.listingId,
      position: 1,
      price: product?.prices?.formattedPrice,
      product_id: product?.productId,
      product_status: '',
      shipping_type: '',
      sku: product?.sku
    }]
  })
}

function generateProductViewEvent(dispatch, product) {
  dispatch(
    analyticsActionCreators.sendProductViewEvent.trigger({
      clientType: HBUS_CLIENT_TYPES.NAVIGATION.SENDBEACON,
      experiments: window?.hepsiBus?.parsers?.getExperiments(),
      canonical_url: '',
      category_hierarchy: product?.categories?.map((item) => (
        {id: item?.categoryId, name: item?.categoryName}
      )),
      merchant: {name: product?.merchant?.name, id: product?.merchant?.id},
      category: {
        id: product?.categories?.[product?.categories?.length - 1]?.categoryId,
        name: product?.categories?.[product?.categories?.length - 1]?.urlKeyword,
      },
      products: [{
        listing_id: product?.listingId,
        position: 1,
        price: product?.price?.formattedPrice,
        product_id: product?.productId,
        product_status: '',
        shipping_type: '',
        sku: product?.sku
      }],
      review_rate: `${product?.reviews?.customerReviewScore || ''}`.replace('.', ','),
      review_count: `${product?.reviews?.customerReviewCount || ''}`,
      layout: '',
    })
  )
  leoProductEvents.sendProductViewEvent({
    product_id: product?.productId,
    price: product?.price?.formattedPrice,
    discount: '',
    sku: product?.sku,
  });
}

function generateProductModel(product, chatOptions) {
  window.productModel = {
    product: {
      sku: product?.sku,
      name: product?.name,
      productId: product?.productId,
      definitionName: product?.definitionName,
      winnerFreight: product?.winnerFreight,
      listings: product?.listings,
      categories: product?.categories,
      currentListing: {
        listingId: product?.listingId,
        stockInformation: product?.stockInformation,
        shipmentInformation: product?.shipmentInformation,
        shippingProfileId: product?.shippingProfileId,
        inStockDate: product?.stockInformation?.inStockDate,
        jetDeliveryCities: product?.jetDeliveryCities,
        tagList: product?.tagList,
        merchantId: product?.merchant?.id,
        merchantName: product?.merchant?.name,
        merchantCountry: product?.merchant?.country,
        merchantCity: product?.merchant?.city,
        currentPrice: {
          value: product?.prices?.[product?.prices?.length - 1].value,
          currency: product?.prices?.[product?.prices?.length - 1].currency
        },
        prices: product?.prices,
      },
      mediaResources: product?.media || [],
    },
    chatOptions
  }
}

function generateMergeUtagData(userInfo, product) {
  window.utagData = {
    ...window.utagData,
    customer_gender_id: userInfo?.gender,
    customer_id: userInfo?.userId,
    customer_login_status: userInfo?.isAuthenticated ? 'logged in' : 'not logged in',
    customer_segments: [],
    mainProduct: {
      listing_id: product?.listingId,
      position: 1,
      price: product?.prices?.[product?.prices?.length - 1]?.formattedPrice,
      product_id: product?.productId,
      product_status: generateProductStatus(product),
      shipping_type: '',
      sku: product?.productId
    }
  }
}

function checkForProductDetailRedirection(responseData, history = null, queryParams) {
  let redirection

  if (!responseData) {
    return null
  }

  if (responseData?.redirection?.url && !process.env.BROWSER) {
    const searchParams = new URLSearchParams();
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        searchParams.append(key, value)
      }
    }
    const queryString = searchParams.toString();

    responseData.redirection.url += responseData.redirection.url.includes('?') ? '&' : '?' + queryString
    redirection = {
      ...responseData.redirection,
    }
  } else if (responseData?.redirection?.url && process.env.BROWSER) {
    return history?.push(responseData?.redirection?.url)
  }

  return redirection
}

function generateCampaignBundleRequest(sku, merchantId) {
  return window?.Campaign?.CAMPAIGN_BUNDLE?.getBundleCampaigns({
    Products: [
      {
        Sku: sku,
        MerchantId: merchantId
      }
    ],
    Platform: 'MobileSite'
  })
}

function generateCampaignSectionRequest(sku, merchantId) {
  //@TODO CONTROL AND DELETE
  return window?.Campaign?.CAMPAIGN_SECTION?.getCampaignSectionComponent({
    product: {
      sku: sku,
      merchantId: merchantId
    }
  })
}

function generateWinnerCampaignRequest(sku, merchantId) {
  //@TODO CONTROL AND DELETE
  return window?.Campaign?.WINNER_CAMPAIGN?.getWinnerCampaignComponent({
    sku: sku,
    merchantId: merchantId
  })
}

function generatePremiumContentRequest(payload) {
  return window?.Loyalty?.fetchAdvantagesByProduct(payload)
}

function generateFollowAndCollectRequest(userId, merchantId, sku) {
  return window?.MerchantContent?.FOLLOW_BUTTON?.getComponentParams({
    sku: sku,
    userId: userId || '',
    merchantId: merchantId
  })
}

function getProductFinalPrice(prices) {
  if (!prices?.length) {
    return null
  }

  const finalPriceIndex = prices.length - 1
  return prices[finalPriceIndex]?.value
}

const updateLocation = (newLocation, {isReplace = false} = {}, dispatch) => {
  if (isReplace) {
    dispatch(replace(newLocation))
  } else {
    dispatch(push(newLocation))
  }
}

const sendProductDetailWebtrekkData = (dispatch, userInfo, webtrekkData) => {
  const data = {
    ...JSON.parse(webtrekkData),
    customSessionParameter: {
      4: userInfo.isAuthenticated ? 'Login' : 'Not Login',
    },
  };

  if (data) {
    dispatch(
      analyticsActionCreators.sendWebtrekkEvent.trigger(data)
    )
  }
}

const generateDeliveryOptionsQueryModel = (product, listings, userInfo) => {
  const location = getBrowserCookie('location') && JSON.parse(getBrowserCookie('location'));

  const createQueryModelData = (item, isProduct = false) => ({
    sku: product?.sku || '',
    listingId: item?.listingId,
    definitionName: product?.definitionName || '',
    warehouseId: item?.warehouseId || item?.stockInformation?.warehouseId,
    shipmentDay: item?.shipmentDay || item?.dispatchTime || item?.shipmentInformation?.shipmentTimeAsDays,
    shippingProfileId: item?.shippingProfileId,
    deci: product?.winnerFreight || null,
    inStockDate: item?.inStockDate || '',
    tags: item?.tagList?.map(tag => tag.tagId) || '',
    isBuyBoxWinner: isProduct || product?.merchantId === item?.merchantId,
    quantity: 1,
    merchantId: item?.merchantId,
    merchantCity: item?.merchantCity || '',
    merchantCountry: item?.merchantCountry || '',
    shipmentDaysPredictedByHb: isNaN(parseInt(item?.aiBasedShipmentDay)) ? null : parseInt(item?.aiBasedShipmentDay),
    customerId: userInfo?.userId || getBrowserCookie('hbus_anonymousId'),
    availableWarehouses: item?.availableWarehouses,
    ...location && item?.jetDeliveryCities?.length > 0 && {
      jetDeliveryCities: item?.jetDeliveryCities
    }
  })

  const queryModelData = [
    createQueryModelData(product, true),
    ...listings?.map(item => createQueryModelData(item)) || []
  ]

  return queryModelData
}

const generateCustomerAddress = (userInfo) => {
  const location = getBrowserCookie('location') && JSON.parse(getBrowserCookie('location'))
  const payload = {
    customerId: userInfo?.userId || '',
    customerLocation: location?.city?.code || '',
    customerCity: location?.city?.name || '',
    customerTown: location?.town?.name || '',
    customerTownCode: location?.town?.code || '',
    customerDistrict: location?.district?.name || '',
    customerDistrictCode: location?.district?.code || '',
    anonymousId: getBrowserCookie('hbus_anonymousId'),
    locationDeliveryUnavailableDays: location?.address?.locationDeliveryUnavailableDays || [],
  }
  if(Number.isInteger(location?.address?.locationType)) {
    payload.locationType = location?.address?.locationType
  }

  return payload
}

const generateDsaModel = (product) => {
  let categoryIds = []
  // eslint-disable-next-line array-callback-return
  product.categories.map((category) => {
    categoryIds.push(category?.categoryId)
  })

  return `DSACategoryId/${categoryIds?.map(item => item).join('/')}`
}

export {
  generateProductDetailCarouselProps,
  generateBuyBoxAreaProps,
  generateBuyBoxSellerProps,
  setAskToSellerButtonAttributes,
  generateVariantPanelProps,
  generateBestReviewProps,
  generateTechSpecsProps,
  generateDeliveryOptionsProps,
  generateProductDescriptionProps,
  generateOtherMerchantsProps,
  generateCampaignBundleProps,
  generatePaymentOptionsProps,
  generateCampaignsProps,
  sendVariantChangeEvent,
  generateProductModel,
  generateProductViewEvent,
  generateQuestionAndAnswerProps,
  generateRecommendationProps,
  generateMergeUtagData,
  checkForProductDetailRedirection,
  generateCampaignBundleRequest,
  generateCampaignSectionRequest,
  generateSponsoredProductsProps,
  generateProductBadgesProps,
  generatePopulerKeywordProps,
  generateMerchantCouponsProps,
  generateVasProps,
  generatePremiumBannerProps,
  generatePremiumContentRequest,
  getProductFinalPrice,
  generateBuybackRowProps,
  generateMinQuantityProps,
  formatPrice,
  updateLocation,
  generateWinnerCampaignRequest,
  sendProductDetailWebtrekkData,
  generateFollowAndCollectRequest,
  generateDeliveryOptionsQueryModel,
  generateCustomerAddress,
  generateDsaModel,
  generateComparedProductsProps,
  generateAdsBannerProps,
}
